import { ReactElement, useEffect, useState } from "react";
import { IUserInfo } from "../../../messages/IAppInfo";
import useApiState, { api } from "../../../hooks/use-api-state";
import Loading from "../../common/Loading";
import ErrorNotification from "../../common/ErrorNotification";
import { ISubCohort, ICohortData } from "../../../messages/IGetCohortByIdResponse";
import styles from "./SubCohorts.module.css";


export default function SubCohorts(props: {
  userInfo: IUserInfo;
  cohort: ICohortData;
}): ReactElement {
  const [apiState, setApiState] = useApiState(false);
  const [editing, setEditing] = useState<number>(-1);
  const [subCohortList, setSubCohortList] = useState<ISubCohort[]>([]);
  const [subCohortNameInputValue, setSubCohortNameInputValue] = useState<string>("");
  const [subCohortIdInputValue, setSubCohortIdInputValue] = useState<string>("");


  useEffect(() => {
    setSubCohortList(props.cohort.subcohorts);
  }, [props.userInfo, props.cohort.subcohorts, setApiState]);


  function resetEditing() {
    setSubCohortNameInputValue("");
    setSubCohortIdInputValue("");
    // Reset tabindex when not editing
    const subCohortList = document.querySelector(`.${styles.subCohortList}`);
    if (subCohortList) {
      Array.from(subCohortList.children).forEach(child => {
        (child as HTMLElement).blur();
      });
    }
    setEditing(-1);
  }


  useEffect(() => {
  if (editing>=0)
    {
      const subCohort = subCohortList[editing];
      setSubCohortNameInputValue(subCohort.name);
      setSubCohortIdInputValue(subCohort.id);
    }
    else
    {
      resetEditing(); // This will not cause infinite loop, as setting editing to -1 if it already is -1 will not invoke the useEffect()
    }
  }, [editing, subCohortList]);


  function saveSubcohorts(newList: ISubCohort[]) {
      api.setSubcohorts(setApiState, props.cohort.id, newList).then((res) => {
        if (res.status==="ok") {
          props.cohort.subcohorts = newList;
        } else {
          setApiState({ isLoading: false, isUnauthenticated: false, errorMessage: "Failed to save subcohorts: " + res.message });
        }
      });
  }


  function setSubCohort() {
    let name = subCohortNameInputValue.trim();
    let id = subCohortIdInputValue.trim();
    if (name && id) {
      let newSubCohortList = [...subCohortList];
      newSubCohortList[editing] = { id, name };
      setSubCohortList(newSubCohortList);
      saveSubcohorts(newSubCohortList);
    }
    resetEditing();
  }


  function addSubCohort() {
    let name = subCohortNameInputValue.trim();
    let id = `group${subCohortList.length + 1}`;
    if (name) {
      let newSubCohortList = [...subCohortList, { id, name }];
      setSubCohortList(newSubCohortList);
      saveSubcohorts(newSubCohortList);
    }
    resetEditing();
  }


  function delSubCohort(index:number) {
    let newSubCohortList = [...subCohortList];
    newSubCohortList.splice(index, 1);
    setSubCohortList(newSubCohortList);
    saveSubcohorts(newSubCohortList);
    resetEditing();
  }


  return (
    <>
      <Loading isLoading={apiState.isLoading}>
        {!!apiState.errorMessage ? (
          <ErrorNotification errorMessage={apiState.errorMessage} />
        ) : (
          !!true && (
            <>
              <div className={styles.subCohortList}>
                {subCohortList.map((subCohort: ISubCohort, index: number) => {
                  return (
                    <div
                      className={styles.subCohortDiv}
                      key={index}
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (event.key === "Escape") {
                          resetEditing();
                        }
                      }}
                    >
                      <p
                        key={subCohort.id}
                        onClick={() => {
                          setEditing(index);
                          setSubCohortNameInputValue(subCohort.name);
                          setSubCohortIdInputValue(subCohort.id);
                        }}
                      >{subCohort.name} ({subCohort.id})</p>
                      <button
                        className={`btn primary`}
                        onClick={() => { delSubCohort(index); }}
                      >Delete</button>
                    </div>
                  ) 
                  }
                )}
              </div>
              <div className={styles.addDiv}>
                <input
                  type="text"
                  className={styles.nameInput}
                  id="add-subcohort-input"
                  value={subCohortNameInputValue}
                  placeholder="Add a subcohort"
                  onChange={(event) => setSubCohortNameInputValue(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      addSubCohort();
                    } else if (event.key === "Escape") {
                      resetEditing();
                    }
                  }}
                />
                {editing>=0 && (
                  <>
                  <span> (</span>
                  <input
                    type="text"
                    className={styles.idInput}
                    id="set-subcohort-id-input"
                    value={subCohortIdInputValue}
                    placeholder="Id"
                    onChange={(event) => setSubCohortIdInputValue(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        setSubCohort();
                      } else if (event.key === "Escape") {
                        resetEditing();
                      }
                    }}
                  />
                  <span>)</span>
                  </>                  
                )}
                <button
                  className={`btn secondary wide`}
                  onClick={() => {
                    if (editing>=0) {
                      setSubCohort();
                    } else {
                      addSubCohort();
                    }
                  }}
                  style={{ whiteSpace: "nowrap" }}
                >
                  { editing>=0 ? "Set" : "Add" }
                </button>
              </div>
            </>
          )
        )}
      </Loading>

    </>
  );
}
